import { useState } from 'react'
import { Link } from 'react-router-dom'
import { MdDeleteOutline, MdEditCalendar, MdEdit } from 'react-icons/md'
import type { SubscriptionLine } from '@/types'
import { formatOrdinalDate } from '@/utils/deliveryDays'
import '@/styles/components/one-off-deliveries.css'
import '@/styles/components/confirm-modal.css'

const formatPrice = (price: number): string => `£${price.toFixed(2)}`

/** Line total and per-unit price, VAT-inclusive for display (matching the rest
 *  of the order). A one-off's price is shown on its own box, never folded into
 *  the weekly total (DF-230). */
const lineTotal = (line: SubscriptionLine): number => (line.subtotal_inc_vat ?? line.subtotal) || 0
const unitPrice = (line: SubscriptionLine): number => (line.unit_price_inc_vat ?? line.unit_price) || 0

/**
 * Past the point a one-off can still be cancelled (DF-230): the dairy has pulled
 * its loading report for that delivery. The backend enforces this too; here the
 * Remove button greys out and explains why on click.
 */
const isPastCutoff = (line: SubscriptionLine): boolean =>
  !!line.cancel_cutoff && new Date() >= new Date(line.cancel_cutoff)

/**
 * Already delivered: the delivery day is in the past, so there is nothing left
 * to cancel and no Remove button is shown at all (DF-230).
 */
const isDelivered = (line: SubscriptionLine): boolean => {
  if (!line.start_date) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return new Date(line.start_date) < today
}

interface OneOffDeliveriesBoxProps {
  lines: SubscriptionLine[]
  /**
   * 'week' is the My Deliveries box attached to a delivery week (copy uses the
   * product name, DF-230). 'management' is the Delivery Management list of all
   * one-off deliveries, with dates and a remove option.
   */
  variant: 'week' | 'management'
  onRemove?: (productId: string) => void
  removeDisabled?: boolean
  /** Move a one-off to another day (management view only, DF-230). */
  onMove?: (productId: string) => void
  moveDisabled?: boolean
  /** View only (the Dynamics view, DF-345): no Edit link. */
  readOnly?: boolean
}

/**
 * One-off products (milk lockers, DF-230) shown in their own white box rather
 * than among the day-by-day products the customer manages. Renders nothing when
 * there are no one-offs to show.
 */
function OneOffDeliveriesBox({
  lines,
  variant,
  onRemove,
  removeDisabled,
  onMove,
  moveDisabled,
  readOnly = false,
}: OneOffDeliveriesBoxProps): React.JSX.Element | null {
  // True while the "too late to cancel" popup is open (shown when a customer
  // clicks a greyed-out Remove). One message at a time is fine.
  const [showTooLate, setShowTooLate] = useState(false)

  if (lines.length === 0) return null

  // Management view: Move and Remove shown together under the name, styled like
  // the day-by-day cards' Pause / Stop. Both grey out past the cut-off and, when
  // clicked then, explain why rather than acting.
  const managementActions = (line: SubscriptionLine): React.JSX.Element | null => {
    if (isDelivered(line) || (!onMove && !onRemove)) return null
    const blocked = isPastCutoff(line)

    return (
      <div className="one-off-box-actions">
        {onMove && (
          <button
            type="button"
            className={`one-off-box-action${blocked ? ' is-blocked' : ''}`}
            onClick={() => (blocked ? setShowTooLate(true) : onMove(line.product_id))}
            disabled={! blocked && moveDisabled}
            aria-disabled={blocked || undefined}
          >
            <MdEditCalendar aria-hidden="true" /> Move
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            className={`one-off-box-action is-delete${blocked ? ' is-blocked' : ''}`}
            onClick={() => (blocked ? setShowTooLate(true) : onRemove(line.product_id))}
            disabled={! blocked && removeDisabled}
            aria-disabled={blocked || undefined}
          >
            <MdDeleteOutline aria-hidden="true" /> Remove
          </button>
        )}
      </div>
    )
  }

  // A single-button info popup, styled like ConfirmModal but with nothing to
  // confirm: the customer can only acknowledge it.
  const tooLatePopup = showTooLate ? (
    <div
      className="modal-backdrop"
      onClick={event => {
        if (event.target === event.currentTarget) setShowTooLate(false)
      }}
    >
      <div className="modal-container confirm-modal" role="dialog" aria-modal="true">
        <button
          type="button"
          className="modal-close-floating"
          onClick={() => setShowTooLate(false)}
          aria-label="Close"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="modal-content is-headless">
          <p className="confirm-message">Sorry, it&apos;s too late to cancel this item.</p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-confirm" onClick={() => setShowTooLate(false)}>
            OK
          </button>
        </div>
      </div>
    </div>
  ) : null

  if (variant === 'week') {
    return (
      <>
      <div className="one-off-box one-off-box--week">
        <div className="one-off-box-header">
          <h3 className="one-off-box-title">One off product deliveries</h3>
          {!readOnly && (
            <Link to="/delivery-management" className="sun-skip-link">
              <MdEdit aria-hidden="true" />
              Edit
            </Link>
          )}
        </div>
        {lines.map(line => (
          <div key={line.id} className="one-off-box-item" data-testid="one-off-week-item">
            <img className="one-off-box-img" src={line.product_image || '/milk-bottle.jpg'} alt="" />
            <div className="one-off-box-body">
              <p className="one-off-box-name">{line.product_name} - One-off delivery</p>
              <p className="one-off-box-sub">
                Your {line.product_name} will be delivered this week.
              </p>
            </div>
            <div className="one-off-box-side">
              {line.quantity > 1 && (
                <span className="one-off-box-qty">{line.quantity} × {formatPrice(unitPrice(line))}</span>
              )}
              <span className="one-off-box-price">{formatPrice(lineTotal(line))}</span>
            </div>
          </div>
        ))}
      </div>
      </>
    )
  }

  return (
    <>
    <section className="one-off-box one-off-box--management" aria-label="One off product deliveries">
      <h2 className="one-off-box-title">One off product deliveries</h2>
      {lines.map(line => (
        <div key={line.id} className="one-off-box-item one-off-box-item--management">
          <img className="one-off-box-img" src={line.product_image || '/milk-bottle.jpg'} alt="" />
          <div className="one-off-box-body">
            <p className="one-off-box-name">{line.product_name}</p>
            <p className="one-off-box-sub">
              Delivered on {line.start_date ? formatOrdinalDate(line.start_date) : 'a scheduled day'}
            </p>
            {managementActions(line)}
          </div>
          <div className="one-off-box-side">
            {line.quantity > 1 && (
              <span className="one-off-box-qty">{line.quantity} × {formatPrice(unitPrice(line))}</span>
            )}
            <span className="one-off-box-price">{formatPrice(lineTotal(line))}</span>
          </div>
        </div>
      ))}
    </section>
    {tooLatePopup}
    </>
  )
}

export default OneOffDeliveriesBox
